/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/esm/Delete';
import AddIcon from '@mui/icons-material/esm/Add';
import EditIcon from '@mui/icons-material/esm/Edit';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { Reference } from '@cedalo/parser';
import JSG from '@cedalo/jsg-ui';
import {
	Expression,
	SheetName,
	SheetReference,
	SheetCommandFactory,
	CellRange
} from '@cedalo/jsg-core';
import SettingsPaper from '../../utils/SettingsPaper';
import { FormGroup, Paper, Stack } from '@mui/material';
import { graphManager } from '../../../GraphManager';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import DialogActions from '@mui/material/DialogActions';
import DialogButton from '../../utils/DialogButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useJSGNotification } from '../../utils/JSGHooks';

const getNames = () => {
	return JSG.sheetNames.names.filter((name) => !name.getName().startsWith('|'));
};

export function NameEdit(props) {
	const [name, setName] = useState('');
	const [formula, setFormula] = useState('');

	useEffect(() => {
		setName(props.name);
	}, [props.name]);

	useEffect(() => {
		setFormula(props.formula);
	}, [props.formula]);

	const handleCancel = () => props.close();

	const handleOk = () => {
		props.onChangeName(name, formula);
		props.close();
	}

	const isFunctionName = (str) => {
		const upper = str.toUpperCase();
		const func = JSG.FormulaParser.context.functionNames.find(funcName => funcName === upper);

		return !!func;
	};

	const isReference = (str) => {
		const range = CellRange.parse(str.toUpperCase(), JSG.streamSheets[0]);

		return !!range;
	};

	const isAlphaNumeric = (str) => {
		let code;

		for (let i = 0; i < str.length; i += 1) {
			code = str.charCodeAt(i);
			if (!(code > 47 && code < 58) && // numeric (0-9)
				!(code > 64 && code < 91) && // upper alpha (A-Z)
				!(code > 96 && code < 123)) { // lower alpha (a-z)
				return false;
			}
		}
		return true;
	};

	const validateName = (newName) => {
		if (JSG.Numbers.canBeNumber(newName)) {
			return (
				<FormattedMessage
					id="NameSettings.Numeric"
					defaultMessage="Invalid name!"
				/>
			)
		}
		if (isFunctionName(newName)) {
			return (
				<FormattedMessage
					id="NameSettings.FunctionName"
					defaultMessage="Function Names are not allowed!"
				/>
			)
		}
		if (isReference(newName)) {
			return (
				<FormattedMessage
					id="NameSettings.ReferenceInvalid"
					defaultMessage="References are not allowed!"
				/>
			)
		}
		if (!isAlphaNumeric(newName)) {
			return (
				<FormattedMessage
					id="NameSettings.AlphaNumeric"
					defaultMessage="Invalid name!"
				/>
			)
		}
		if (!newName.length && !Reference.isValidIdentifier(newName)) {
			return (
				<FormattedMessage
					id="NameSettings.InvalidName"
					defaultMessage="Invalid name!"
				/>
			)
		}
		if (JSG.sheetNames.get(newName)) {
			if (props.open === 2 && newName === props.oldName) {
				return undefined;
			}
			return (
				<FormattedMessage
					id="NameSettings.Unique"
					defaultMessage="Name must be unique!"
				/>
			)
		}

		return undefined
	}

	const validateFormula = (newFormula) => {
		if (newFormula && newFormula.length) {
			newFormula = newFormula.substring(1);

			try {
				const term = JSG.FormulaParser.parseSheet(newFormula.toUpperCase(), JSG.streamSheets[0]);
				if (term && term.operand instanceof SheetReference) {
					return undefined;
				}
				// eslint-disable-next-line no-empty
			} catch(e) {

			}
		}

		return (
			<FormattedMessage
				id="NameSettings.ReferenceAllowed"
				defaultMessage="Invalid reference!"
			/>
		)
	}

	return (
		<Dialog open={props.open !== 0} onClose={handleCancel} /* onEscapeKeyDown={handleCancel} */>
			<DialogTitle>
				<FormattedMessage id="NameSettings.EditNameTitle" defaultMessage="Edit Name" />
			</DialogTitle>
			<DialogContent
				style={{
					margin: '20px'
				}}
			>
				<FormGroup>
					<Typography>
						<FormattedMessage id="NameSettings.EditNameInfo" defaultMessage="Please enter a unique name and a reference using an equal sign:" />
					</Typography>
				<TextField
					variant="outlined"
					size="small"
					margin="normal"
					value={name}
					onChange={(event) => setName(event.target.value)}
					label={<FormattedMessage id="NameSettings.Name" defaultMessage="Name" />}
					error={!!validateName(name)}
					helperText={validateName(name)}
				/>
				<TextField
					variant="outlined"
					size="small"
					margin="normal"
					value={formula}
					onChange={(event) => setFormula(event.target.value)}
					label={<FormattedMessage id="NameSettings.Reference" defaultMessage="Formula" />}
					error={!!validateFormula(formula)}
					helperText={validateFormula(formula)}
				/>
				</FormGroup>
			</DialogContent>
			<Divider />
			<DialogActions>
				<DialogButton margin onClick={handleCancel}>
					<FormattedMessage id="Dialog.Cancel" defaultMessage="Cancel" />
				</DialogButton>
				<DialogButton autoFocus margin disabled={validateName(name) || validateFormula(formula)} onClick={handleOk}>
					<FormattedMessage id="Dialog.OK" defaultMessage="OK" />
				</DialogButton>
			</DialogActions>
		</Dialog>
	);
}

export function NameSettings(props) {
	const [selected, setSelected] = useState('');
	const [names, setNames] = useState(getNames());
	const [openEdit, setOpenEdit] = useState(0);
	const [editName, setEditName] = useState('');
	const [editFormula, setEditFormula] = useState('');

	useEffect(() => {
		setNames(getNames());
	}, [props.open]);

	useJSGNotification('NAMED_CELLS_NOTIFICATION', () => setNames(getNames()));

	const handleAdd = () => {
		setOpenEdit(1);

		let name = 'Name1';
		let i = 1;

		while (JSG.sheetNames.get(name)) {
			name = `Name${i}`;
			i += 1;
		}

		setEditName(name);
		const newFormula = graphManager.graph.sheetSelection.toString({ forceName: true });

		setEditFormula(newFormula);
	};

	const handleDelete = () => {
		const nameItem = JSG.sheetNames.get(selected);
		if (nameItem) {
			const cmd = SheetCommandFactory.create('command.DeleteSheetNameCommand', graphManager.graph, nameItem);
			graphManager.synchronizedExecute(cmd);
		}
	};

	const handleChangeName = (name, formula) => {
		let cmd;

		if (formula.length && formula[0] === '=') {
			formula = formula.substring(1);
		}

		if (openEdit === 1) {
			cmd = SheetCommandFactory.create(
				'command.AddSheetNameCommand',
				graphManager.graph,
				new SheetName(name, new Expression(0, formula))
			);
		} else {
			const nameItem = JSG.sheetNames.get(selected);
			if (nameItem) {
				cmd = SheetCommandFactory.create(
					'command.SetSheetNameCommand',
					graphManager.graph,
					nameItem,
					name,
					new Expression(0, formula)
				);
			}
		}
		if (cmd) {
			graphManager.synchronizedExecute(cmd);
		}
	}

	const handleEdit = () => {
		const nameItem = JSG.sheetNames.get(selected);
		if (nameItem) {
			setEditName(nameItem.getName());
			setEditFormula(nameItem.getFormula());
			setOpenEdit(2);
		}
	};

	const handleClose = () => {
		props.close();
	};

	if (!props.open) {
		return null;
	}

	return (
		<SettingsPaper
			open={props.open}
			close={handleClose}
			title={<FormattedMessage id="NameSettings.Title" defaultMessage="Names" />}
			helpContext="guides/app/names"
		>
			<FormGroup>
				<Stack direction="row" spacing={1} sx={{flexDirection: 'row-reverse'}}>
					<IconButton onClick={handleDelete} size="small" disabled={!selected}>
						<DeleteIcon/>
					</IconButton>
					<IconButton onClick={handleEdit} size="small" disabled={!selected}>
						<EditIcon/>
					</IconButton>
					<IconButton onClick={handleAdd} size="small">
						<AddIcon/>
					</IconButton>
				</Stack>
				<Paper
					variant="outlined"
					style={{
						marginTop: '10px',
						height: '500px',
						overflowY: 'scroll'
					}}
				>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>
									<FormattedMessage id="NameSettings.Name" defaultMessage="Name" />
								</TableCell>
								<TableCell>
									<FormattedMessage id="NameSettings.Reference" defaultMessage="Reference" />
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{names.map((name) => (
								<TableRow
									onClick={() => setSelected(name.getName())}
									onDoubleClick={handleEdit}
									selected={selected=== name.getName()}
									key={name.getName()}
									sx={{ cursor: 'default', '&:last-child td, &:last-child th': { border: 0 } }}
								>
									<TableCell key={1}>
										{name.getName()}
									</TableCell>
									<TableCell key={2}>
										{`=${name.getFormula()}`}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Paper>
				<NameEdit
					open={openEdit}
					close={() => setOpenEdit(0)}
					name={editName}
					oldName={openEdit === 2 ? selected : undefined}
					onChangeName={(name, formula) => handleChangeName(name, formula)}
					formula={`=${editFormula}`}
				/>
			</FormGroup>
		</SettingsPaper>
	);
}
